import { alpha, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { commonStyles, covConstantsStyles } from '~/modules/guarantees/legacy/styles.ts';

type TApplicationsTableCellSizeKey = 'applicationNumber' | 'createdAt';

export const guaranteesClientPageStyles = {
  pageRoot: (theme: Theme) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    paddingTop: { xs: theme.spacing(2), sm: theme.spacing(4) },
    paddingBottom: { xs: theme.spacing(2), sm: theme.spacing(4) },
    fontFamily: '"Nunito Sans", sans-serif',
    overflowX: 'hidden',
  }),
  pageContainer: (theme: Theme) => ({
    width: '100%',
    maxWidth: `${theme.breakpoints.values.lg}px`,
  }),
  pageTitle: (theme: Theme) => ({
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: { xs: '1.5rem', sm: '2rem' },
  }),
  pageSubtitle: (theme: Theme) => ({
    ...commonStyles.subtitle(theme),
    color: theme.palette.text.secondary,
  }),
  headerActions: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  }),
  applicationWithStatusSidebar: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(280px, 340px)' },
    alignItems: 'start',
    gap: { xs: theme.spacing(1.5), lg: theme.spacing(2) },
    minWidth: 0,
  }),
  applicationStatusSidebarMobile: {
    display: { xs: 'block', lg: 'none' },
    minWidth: 0,
  },
  applicationStatusSidebarDesktop: (theme: Theme) => ({
    display: { xs: 'none', lg: 'block' },
    position: 'sticky',
    top: theme.spacing(16),
    minWidth: 0,
  }),
  applicationSidePanelStack: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: theme.spacing(1.5), lg: theme.spacing(2) },
    minWidth: 0,
    width: '100%',
  }),
  statusFlowWrap: (theme: Theme) => ({
    width: '100%',
    boxSizing: 'border-box',
    mx: 0,
    mt: 0,
    mb: { xs: theme.spacing(1.5), lg: 0 },
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  }),
  statusFlowHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    pt: { xs: theme.spacing(3.5), sm: theme.spacing(3.8) },
    pb: { xs: theme.spacing(3), sm: theme.spacing(3.375) },
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
  }),
  statusFlowBody: (theme: Theme) => ({
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
  }),
  statusFlowTitle: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 800,
    textAlign: 'center',
  }),
  statusFlowList: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
    width: '100%',
  }),
  statusFlowNode: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    outline: 'none',
  }),
  statusFlowTransition: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minHeight: 24,
    pl: theme.spacing(1.25),
    color: theme.palette.text.secondary,
  }),
  statusFlowArrow: (theme: Theme) => ({
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: 22,
  }),
  statusFlowDate: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  }),
  statusFlowLoading: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    width: '100%',
    minHeight: 28,
  }),
  statusFlowLoadingText: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '18px',
  }),
  statusFlowTooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  statusFlowTooltipLine: {
    color: 'text.primary',
    fontSize: '0.8125rem',
    lineHeight: '16px',
  },
  statusFlowTooltipDescription: (theme: Theme) => ({
    pt: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '16px',
  }),
  applicationFilesList: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
    width: '100%',
  }),
  applicationFilesItem: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(1.25),
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1),
    backgroundColor: alpha(theme.palette.grey[50], 0.72),
  }),
  applicationFilesItemHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
    flexDirection: { xs: 'column', sm: 'row' },
  }),
  applicationFilesType: {
    minWidth: 0,
    color: 'text.primary',
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 800,
    wordBreak: 'break-word',
  },
  applicationFilesTypeActions: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    flexShrink: 0,
    width: { xs: '100%', sm: 'auto' },
  }),
  applicationFilesRows: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
  }),
  applicationFilesRow: (theme: Theme) => ({
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
    flexDirection: { xs: 'column', sm: 'row' },
    paddingTop: theme.spacing(0.75),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  }),
  applicationFilesName: {
    minWidth: 0,
    color: 'text.secondary',
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 700,
    wordBreak: 'break-word',
  },
  applicationFilesRowActions: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    flexShrink: 0,
    width: { xs: '100%', sm: 'auto' },
  }),
  applicationFilesActionButton: {
    minHeight: 30,
    px: 1,
    fontSize: '0.75rem',
    lineHeight: '16px',
    fontWeight: 800,
    textTransform: 'none',
  },
  applicationFilesHiddenInput: {
    display: 'none',
  },
  applicationFilesEmptyText: {
    color: 'text.disabled',
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 700,
  },
  applicationHeaderMeta: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'flex-start', sm: 'flex-end' },
    gap: theme.spacing(0.5),
    minWidth: 0,
  }),
  detailLabel: {
    color: 'text.secondary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '0.875rem',
    lineHeight: '20px',
    fontWeight: 700,
  },
  detailValue: {
    marginTop: 0.5,
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: '22px',
    fontWeight: 400,
    wordBreak: 'break-word',
  },
  applicationsTable: {
    minWidth: 680,
  },
  applicationsTableCellSize: {
    applicationNumber: { width: '30%' },
    createdAt: { width: '24%' },
  } as Record<TApplicationsTableCellSizeKey, { width: string }>,
  applicationNumberText: {
    ...covConstantsStyles.tablePrimaryText,
    fontWeight: 700,
    color: 'primary.main',
  },
  helperText: {
    mt: 1.5,
    color: 'text.secondary',
  },
  applicationFlowStack: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  }),
  flowSectionPaper: (theme: Theme) => ({
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.border.subtle}`,
    boxShadow: 'none',
    overflow: 'hidden',
  }),
  flowSectionPaperDisabled: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    opacity: 0.72,
  }),
  flowSectionHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    padding: { xs: theme.spacing(2), sm: theme.spacing(2, 2.5) },
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.background.tableHeader ?? theme.palette.common.white,
  }),
  flowSectionHeaderContent: {
    flex: 1,
    minWidth: 0,
  },
  flowSectionNumber: (theme: Theme) => ({
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 36,
    minHeight: 27,
    paddingInline: theme.spacing(1.5),
    borderRadius: '100px',
    backgroundColor: theme.palette.primary['200'],
    color: theme.palette.primary.main,
    fontWeight: 800,
    fontSize: '0.75rem',
    lineHeight: '16px',
  }),
  flowSectionTitle: (theme: Theme) => ({
    color: theme.palette.text.primary,
    fontWeight: 800,
    fontSize: '0.9375rem',
    lineHeight: '20px',
  }),
  flowSectionDescription: (theme: Theme) => ({
    mt: theme.spacing(0.375),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '20px',
  }),
  flowSectionBody: (theme: Theme) => ({
    padding: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
  }),
  flowFieldsGrid: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
    gap: theme.spacing(1.5),
  }),
  flowFullWidthField: {
    gridColumn: { xs: 'auto', md: '1 / -1' },
  },
  flowField: {
    width: '100%',
  },
  flowAccountMode: (theme: Theme) => ({
    mt: theme.spacing(1.5),
  }),
  flowAccountSubsection: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    mt: theme.spacing(2),
    pt: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.border.subtle}`,
    minWidth: 0,
  }),
  flowAccountSubsectionTitle: {
    color: 'text.primary',
    fontSize: '0.875rem',
    lineHeight: '20px',
    fontWeight: 800,
    wordBreak: 'break-word',
  },
  flowAccountInlineRow: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'minmax(260px, 360px) minmax(0, 1fr)',
    },
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  }),
  flowAccountInlineField: {
    minWidth: 0,
    width: '100%',
  },
  flowManualAccountFieldWrap: (theme: Theme) => ({
    width: '100%',
    mt: theme.spacing(1.5),
  }),
  flowManualAccountField: {
    display: 'block',
    width: '100%',
    maxWidth: 'none',
    '& .MuiFormControl-root': {
      width: '100%',
      maxWidth: 'none',
    },
    '& .MuiInputBase-root': {
      width: '100%',
    },
  },
  flowLongAutocompleteField: (theme: Theme) => ({
    gridColumn: { xs: 'auto', md: '1 / -1' },
    width: '100%',
    '& .MuiAutocomplete-input': {
      fontSize: '0.8125rem',
      lineHeight: '18px',
    },
    '& .MuiAutocomplete-option': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      lineHeight: '18px',
      py: theme.spacing(1),
    },
  }),
  flowPhoneField: (theme: Theme) => ({
    width: '100%',
    minWidth: 0,
    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& .MuiFormControl-root > .MuiInputLabel-root': {
      position: 'static',
      transform: 'none',
      width: 'auto',
      maxWidth: '100%',
      marginBottom: 0,
      color: theme.palette.text.primary,
      fontSize: '0.75rem',
      lineHeight: '14px',
      fontWeight: 700,
      whiteSpace: 'normal',
    },
    '& .MuiFormControl-root > .MuiBox-root': {
      marginTop: 0,
    },
    '& .MuiFormControl-root > .MuiFormHelperText-root': {
      marginTop: theme.spacing(1),
    },
  }),
  flowBusinessTable: {
    minWidth: 1080,
  },
  flowAccountTable: {
    minWidth: 760,
  },
  flowSelectableTableRow: (isSelected: boolean) => (theme: Theme) => ({
    cursor: 'pointer',
    backgroundColor: isSelected ? theme.palette.primary['200'] : theme.palette.common.white,
    '&:hover': {
      backgroundColor: isSelected ? theme.palette.primary['200'] : alpha(theme.palette.primary.main, 0.04),
    },
    '& td': {
      backgroundColor: isSelected ? theme.palette.primary['200'] : theme.palette.common.white,
    },
    '&:hover td': {
      backgroundColor: isSelected ? theme.palette.primary['200'] : alpha(theme.palette.primary.main, 0.04),
    },
    '&:last-of-type td': {
      borderBottom: 0,
    },
  }),
  flowTableSelectButton: (theme: Theme) => ({
    minHeight: 32,
    px: theme.spacing(2.25),
    borderRadius: '100px',
    borderColor: theme.palette.border.subtle,
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      boxShadow: 'none',
    },
    '&.MuiButton-contained': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  }),
  flowActions: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    mt: theme.spacing(2),
  }),
  flowLoadingState: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minHeight: 64,
  }),
  flowPageActions: (theme: Theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    pt: theme.spacing(0.5),
  }),
  flowPageActionsCentered: (theme: Theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    pt: theme.spacing(0.5),
  }),
  flowInlineActionRow: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
  }),
  flowSecondaryAction: (theme: Theme) => ({
    mt: { xs: 0, sm: theme.spacing(2.75) },
    minHeight: 48,
    borderRadius: '100px',
    px: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  }),
  flowOutlinedPrimaryAction: (theme: Theme) => ({
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      borderColor: theme.palette.primary.dark,
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      borderColor: theme.palette.action.disabledBackground,
    },
  }),
  flowLockHint: (theme: Theme) => ({
    mt: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '18px',
  }),
  flowInlineTooltipLabel: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    lineHeight: 1,
    verticalAlign: 'middle',
  }),
  flowInlineTooltipIcon: (theme: Theme) => ({
    width: theme.spacing(1.75),
    height: theme.spacing(1.75),
    color: theme.palette.primary.main,
  }),
  flowLoadingCell: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.25),
    color: theme.palette.text.secondary,
  }),
  generatedDocumentPreview: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    p: theme.spacing(1.5),
    bgcolor: 'background.default',
    color: 'text.secondary',
    border: '2px solid',
    borderColor: 'text.secondary',
    borderRadius: '10px',
    [theme.breakpoints.down(820)]: {
      border: 'none',
      borderRadius: 0,
      p: 0,
      bgcolor: 'transparent',
    },
  }),
  generatedDocumentIframe: (theme: Theme) => ({
    width: '100%',
    height: '600px',
    border: 'none',
    [theme.breakpoints.down(820)]: {
      display: 'none',
    },
  }),
  generatedDocumentActions: (theme: Theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    mt: theme.spacing(2),
    [theme.breakpoints.down(820)]: {
      mt: 0,
    },
  }),
  generatedDocumentOpenButton: {
    minWidth: '280px',
  },
  tenderSummaryPanel: (theme: Theme) => ({
    mt: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.common.white,
  }),
  tenderSummaryTitle: {
    color: 'text.primary',
    fontWeight: 800,
    fontSize: '1rem',
    lineHeight: '22px',
    wordBreak: 'break-word',
  },
  tenderPrimaryValue: (theme: Theme) => ({
    mt: theme.spacing(1.25),
    padding: theme.spacing(1.25),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary['200'],
    border: `1px solid ${theme.palette.primary['200'] ?? theme.palette.border.subtle}`,
  }),
  tenderPrimaryValueText: (theme: Theme) => ({
    color: theme.palette.primary.main,
    fontWeight: 900,
    fontSize: '1.125rem',
    lineHeight: '24px',
  }),
  tenderSummaryMetaGrid: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      lg: 'repeat(3, minmax(0, 1fr))',
    },
    gap: theme.spacing(1),
    mt: theme.spacing(1.25),
  }),
  tenderLotsHeader: (theme: Theme) => ({
    mt: theme.spacing(2),
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  }),
  flowOptionsList: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(0.75),
    mt: theme.spacing(1),
  }),
  flowLotCard:
    (isSelected: boolean) =>
    (theme: Theme): SystemStyleObject<Theme> => ({
      display: 'grid',
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr) auto',
        sm: 'minmax(0, 1fr) minmax(240px, 320px) auto',
      },
      alignItems: 'center',
      gap: theme.spacing(1.25),
      minWidth: 0,
      width: '100%',
      textAlign: 'left',
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.spacing(1),
      border: `1px solid ${
        isSelected ? theme.palette.primary.main : theme.palette.border.subtle
      }`,
      backgroundColor: isSelected ? theme.palette.primary['200'] : theme.palette.common.white,
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: isSelected
          ? theme.palette.primary['200']
          : alpha(theme.palette.primary.main, 0.04),
      },
      '&.Mui-disabled': {
        opacity: 0.7,
      },
    }),
  flowOptionTitle: {
    color: 'text.primary',
    fontWeight: 800,
    fontSize: '0.9375rem',
    lineHeight: '20px',
    wordBreak: 'break-word',
  },
  flowOptionSource: (theme: Theme) => ({
    mt: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: '18px',
  }),
  flowOptionMetaGrid: (theme: Theme) => ({
    display: 'grid',
    gridColumn: { xs: '1 / -1', sm: 'auto' },
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
    },
    gap: theme.spacing(1),
    mt: { xs: theme.spacing(1.25), sm: 0 },
  }),
  flowOptionMetaItem: (theme: Theme) => ({
    minWidth: 0,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.75),
    border: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.common.white,
  }),
  flowLotMetaItem: {
    minWidth: 0,
  },
  flowLotCheckbox: (theme: Theme) => ({
    justifySelf: 'end',
    color: theme.palette.text.secondary,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  }),
  tenderLotsTotal: (theme: Theme) => ({
    mt: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.background.default,
  }),
  tenderLotsTotalValue: (theme: Theme) => ({
    color: theme.palette.primary.main,
    fontWeight: 900,
    fontSize: '1rem',
    lineHeight: '22px',
    textAlign: 'right',
  }),
} as const;
